// useWhatsNew — 「新機能の紹介」の開閉 state と未読判定。
// 開いたら全件を既読にする (= 未読の印は開いた瞬間に消える)。ダイアログ内の「新着」表示は
// 開いた時点の未読 ID をスナップショットして使う。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  hasUnreadWhatsNew,
  sortWhatsNewNewestFirst,
  type WhatsNewEntry,
} from '../../domain/whatsNew.js';
import {
  createWhatsNewSeenStore,
  type WhatsNewSeenStore,
} from '../../infrastructure/whatsNewSeen.js';
import { WHATS_NEW_ENTRIES } from '../whatsNewEntries.js';

export interface WhatsNewController {
  readonly open: boolean;
  readonly openDialog: () => void;
  readonly closeDialog: () => void;
  /** 新しい順。 */
  readonly entries: ReadonlyArray<WhatsNewEntry>;
  /** 未読の印を出すか。 */
  readonly hasUnread: boolean;
  /** 今回開いたときに未読だった ID (ダイアログの「新着」印用)。 */
  readonly newIds: ReadonlySet<string>;
}

export interface WhatsNewAutoOpenOptions {
  /** true になったら 1 回だけ開く (入室直後の自動表示、useWhatsNewEntryAutoOpen が決める)。 */
  readonly autoOpen?: boolean;
  /** 自動で開いたあとに呼ぶ (RoomLayout 側のフラグを消費する)。 */
  readonly onAutoOpened?: () => void;
}

export const useWhatsNew = (
  allEntries: ReadonlyArray<WhatsNewEntry> = WHATS_NEW_ENTRIES,
  storeOverride?: WhatsNewSeenStore,
  autoOpenOptions: WhatsNewAutoOpenOptions = {},
): WhatsNewController => {
  const storeRef = useRef<WhatsNewSeenStore | null>(null);
  if (storeRef.current === null) storeRef.current = storeOverride ?? createWhatsNewSeenStore();
  const store = storeRef.current;

  const entries = useMemo(() => sortWhatsNewNewestFirst(allEntries), [allEntries]);
  const [seenIds, setSeenIds] = useState<ReadonlySet<string>>(() => store.readSeenIds());
  const [open, setOpen] = useState(false);
  const [newIds, setNewIds] = useState<ReadonlySet<string>>(() => new Set());

  const openDialog = useCallback(() => {
    const seen = store.readSeenIds();
    setNewIds(new Set(entries.filter((e) => !seen.has(e.id)).map((e) => e.id)));
    store.markSeen(entries.map((e) => e.id));
    // storage 不可の環境では永続されないが、このページ滞在中は印を消す。
    setSeenIds(new Set([...seen, ...entries.map((e) => e.id)]));
    setOpen(true);
  }, [store, entries]);
  const closeDialog = useCallback(() => setOpen(false), []);

  // 入室直後の自動表示。レイアウト mount 時に autoOpen が立っていれば開いて消費する
  // (Classic ⇄ Modern 切替の再 mount では、消費済みなので二度開かない)。
  const { autoOpen = false, onAutoOpened } = autoOpenOptions;
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!autoOpen || autoOpenedRef.current) return;
    // StrictMode の effect 二重実行で 2 回目に「新着」印が消えないよう 1 hook 1 回に絞る。
    autoOpenedRef.current = true;
    openDialog();
    onAutoOpened?.();
  }, [autoOpen, openDialog, onAutoOpened]);

  return {
    open,
    openDialog,
    closeDialog,
    entries,
    hasUnread: hasUnreadWhatsNew(entries, seenIds),
    newIds,
  };
};
